import React, {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import _ from 'lodash';
import Pagination from '@/components/pagination-new/Pagination';
import useScrollToTop from '@/hooks/useScrollToTop';
import useFilterCount from '@/hooks/useFilterCount';
import useQuery from './useQuery';
import FilterButton from '@/pages/_common_filters/FilterButton';
import PaginationInformation from '@/components/pagination-new/PaginationInformation';
import SortBy from './SortBy';
import {attributeKeys} from './queryUtils';
import useDialogLauncher from '@hooks/@lore/dialogs-routable/_hooks/useDialogLauncher';
import useDialogConfig from '@hooks/@lore/dialogs-routable/_hooks/useDialogConfig';
import {DialogManager} from '@/dialogs_new/DialogManager';
import View from '@/pages/_components/View';
import {CloudArrowDownIcon} from '@heroicons/react/24/outline';
import DistanceFilter, {RADIUS_OPTIONS, toLocationCondition} from "@/components/ui/DistanceFilter";
import getSearchParamsFromQuery from "@/utils/user/getSearchParamsFromQuery";

// Applied when the url has no explicit value; never written to the url.
const DEFAULT_LOCATION_CONDITION = toLocationCondition(
  RADIUS_OPTIONS.find(option => option.id === 'le-50')
);
const DEFAULT_ORDER = 'distance asc';

Partners.propTypes = {
  children: PropTypes.func.isRequired,
  router: PropTypes.shape({
    location: PropTypes.shape({
      search: PropTypes.string,
      pathname: PropTypes.string
    }),
    history: PropTypes.shape({
      push: PropTypes.func
    }),
    navigate: PropTypes.func
  }).isRequired,
  site: PropTypes.shape({
    data: PropTypes.shape({})
  })
};

export default function Partners(props) {
  const { router, children, site } = props;
  const { location, history, navigate } = router;

  const search = queryString.parse(location.search);
  const filterCount = useFilterCount(search, _.filter(attributeKeys, key => [
    'networks',
    'locationCondition'
  ].indexOf(key) < 0));

  const locationCondition = search.locationCondition ?
    getSearchParamsFromQuery(search).locationCondition :
    DEFAULT_LOCATION_CONDITION;

  // The defaults live here rather than in the url, so the query sees them even
  // though the filter controls only ever write explicit choices.
  const searchWithDefaults = {
    ...search,
    order: search.order || DEFAULT_ORDER,
    locationCondition: JSON.stringify(locationCondition)
  };

  // Exception: the sort order is written back to the url, so SortBy reflects it.
  // Replaced rather than pushed, to keep it out of the back button history.
  useEffect(() => {
    if (!search.order) {
      navigate({
        pathname: location.pathname,
        search: queryString.stringify({
          ...search,
          order: DEFAULT_ORDER
        })
      }, { replace: true });
    }
  }, [location.search]);

  const node = useRef();
  const scrollToTop = useScrollToTop();

  const pageSize = 12;

  const result = useQuery({
    search: searchWithDefaults,
    pageSize,
    site: site?.data
  });

  const csvQuery = useQuery({
    search: searchWithDefaults,
    returnCsvQuery: true
  });

  const launch = useDialogLauncher();
  const config = useDialogConfig();

  function showFilters() {
    launch(DialogManager.FiltersPartner.Config(config?.id));
  }

  function onDownload(query) {
    launch(DialogManager.CSVDownload.Config(query, { skipNetworkFilter: true }));
  }

  function onChangeLocationCondition(option) {
    const search = queryString.parse(location.search) || {};

    // Always written, including "anywhere", so an explicit choice is
    // distinguishable from the default applied when the param is absent.
    search.locationCondition = JSON.stringify(toLocationCondition(option));

    history.push({
      pathname: location.pathname,
      search: queryString.stringify({
        ...search,
        page: 1
      })
    });
  }


  return (
    <>
      <div ref={node} className="flex flex-wrap gap-4 justify-between items-end">
        <PaginationInformation
          pageSize={pageSize}
          totalCount={result.meta?.totalCount}
        />
        <div className="flex flex-wrap gap-4">
          <DistanceFilter
            selectedId={locationCondition.id}
            onChange={onChangeLocationCondition}
          />
          <SortBy withDistance={true}/>
          <FilterButton
            count={filterCount}
            onClick={showFilters}
          />
        </div>
      </div>
      {children(result)}
      <div className="space-y-4">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <PaginationInformation
            pageSize={pageSize}
            totalCount={result.meta?.totalCount}
          />
          <Pagination
            pageSize={pageSize}
            totalCount={result.meta?.totalCount}
            onNavigate={() => scrollToTop(node)}
          />
        </div>
        {result.meta?.totalCount > 0 && (
          <div className="flex items-center">
            <View
              icon={CloudArrowDownIcon}
              onClick={() => onDownload(csvQuery)}
            >
              Download results as CSV
            </View>
          </div>
        )}
      </div>
    </>
  );
}
